import React from 'react';
import { CouponScopeColors } from '../../../../api/payments/coupons/domain/labels';
import { formatCurrency } from '../../../../helpers/formatCurrency';
import { CouponScopeTag, getScopeAccent } from './CouponScopeTag';
import { formatRuleValue, ruleActiveAtCycle } from '../utils';

const MIN_VISIBLE_CYCLES = 6;
const MAX_VISIBLE_CYCLES = 24;

const cellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'var(--space-32)',
  fontSize: 'var(--fs-14)',
};

const toCycleNumber = (value, fallback) =>
  value == null || value === '' || !Number.isFinite(Number(value))
    ? fallback
    : Number(value);

// Mapa regra × ciclo: uma barra por regra sobre as colunas de ciclo (a última
// coluna, ∞, é "do ciclo N+1 em diante"). A linha "Ativos" conta quantas regras
// valem em cada ciclo — ≥2 é sobreposição. getCycleTotal(ciclo), quando
// informado, adiciona a linha com o valor cobrado por ciclo (oferta).
// minVisible: piso de ciclos visíveis, pra alinhar colunas entre vários mapas.
export function CycleDiscountMap({
  rules,
  getTarget,
  getCycleTotal,
  totalLabel = 'Total cobrado',
  onRuleClick,
  minVisible = 0,
}) {
  if (!rules.length) return null;

  const bounds = rules.flatMap((rule) => [
    toCycleNumber(rule.fromCycle, 1),
    toCycleNumber(rule.toCycle, 0),
  ]);
  const visible = Math.min(
    MAX_VISIBLE_CYCLES,
    Math.max(MIN_VISIBLE_CYCLES, minVisible, ...bounds),
  );
  const cycles = Array.from({ length: visible }, (_, i) => i + 1);
  const columns = [...cycles, visible + 1];
  // Colunas: 1 = rótulo, 2..visible+1 = ciclos, visible+2 = ∞.
  const infinityLine = visible + 3;

  const counts = columns.map(
    (cycle) => rules.filter((rule) => ruleActiveAtCycle(rule, cycle)).length,
  );
  const totals = getCycleTotal ? columns.map(getCycleTotal) : null;

  const header = (label, column) => (
    <div
      key={label}
      className="text-muted"
      style={{ ...cellStyle, gridColumn: column, gridRow: 1 }}
    >
      {label}
    </div>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        role="group"
        aria-label="Descontos por ciclo"
        style={{
          display: 'grid',
          gridTemplateColumns: `minmax(160px, 220px) repeat(${
            columns.length
          }, minmax(${getCycleTotal ? 76 : 40}px, 1fr))`,
          rowGap: 'var(--space-4)',
          minWidth: 'fit-content',
        }}
      >
        <div
          className="text-muted"
          style={{ ...cellStyle, gridRow: 1, justifyContent: 'flex-start' }}
        >
          Ciclo
        </div>
        {cycles.map((cycle) => header(cycle, cycle + 1))}
        {header('∞', visible + 2)}

        {rules.map((rule, index) => {
          // Acima do limite visível, a barra é comprimida na coluna ∞.
          const from = Math.min(toCycleNumber(rule.fromCycle, 1), visible + 1);
          const rawTo = toCycleNumber(rule.toCycle, null);
          const to = rawTo === null ? null : Math.min(rawTo, visible + 1);
          const openEnded = to === null || rawTo > visible;
          const row = index + 2;
          const target = getTarget?.(rule);
          const endLine = to === null ? infinityLine : Math.max(to, from) + 2;
          const accent = getScopeAccent(rule.scope);
          const tone = CouponScopeColors[rule.scope] ?? 'neutral';
          return (
            <React.Fragment key={rule._key ?? rule.id ?? index}>
              <div
                className="d-flex align-items-center flex-wrap"
                style={{ gridColumn: 1, gridRow: row, gap: 'var(--space-8)' }}
              >
                <CouponScopeTag scope={rule.scope} />
                {target && <span className="text-muted">{target}</span>}
              </div>
              <button
                type="button"
                onClick={onRuleClick ? () => onRuleClick(rule) : undefined}
                disabled={!onRuleClick}
                title={`Desconto ${index + 1}`}
                style={{
                  gridColumn: `${from + 1} / ${endLine}`,
                  gridRow: row,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 var(--space-8)',
                  minHeight: 'var(--space-32)',
                  border: 'none',
                  borderLeft: `4px solid ${accent}`,
                  borderRadius: 'var(--radius-4, 4px)',
                  background: `var(--bg-${tone}-subtler, var(--bg-neutral-layer02))`,
                  color: 'var(--fg-neutral-default)',
                  fontFamily: 'inherit',
                  fontWeight: 'var(--fw-medium)',
                  fontSize: 'var(--fs-14)',
                  cursor: onRuleClick ? 'pointer' : 'default',
                }}
              >
                <span>{formatRuleValue(rule)}</span>
                {openEnded && <span aria-hidden="true">▶</span>}
              </button>
            </React.Fragment>
          );
        })}

        <div
          className="text-muted"
          style={{
            ...cellStyle,
            gridColumn: 1,
            gridRow: rules.length + 2,
            justifyContent: 'flex-start',
          }}
        >
          Descontos ativos
        </div>
        {counts.map((count, i) => (
          <div
            key={columns[i]}
            title={`${count} ${
              count === 1 ? 'desconto ativo' : 'descontos ativos'
            }`}
            style={{
              ...cellStyle,
              gridColumn: i + 2,
              gridRow: rules.length + 2,
              borderRadius: 'var(--radius-4, 4px)',
              background:
                count > 1 ? 'var(--bg-warning-subtler)' : 'transparent',
              color: count === 0 ? 'var(--fg-neutral-subtle)' : undefined,
              fontWeight: count > 1 ? 'var(--fw-medium)' : undefined,
            }}
          >
            {count === 0 ? '—' : count}
          </div>
        ))}

        {totals && (
          <>
            <div
              className="text-muted"
              style={{
                ...cellStyle,
                gridColumn: 1,
                gridRow: rules.length + 3,
                justifyContent: 'flex-start',
              }}
            >
              {totalLabel}
            </div>
            {totals.map((total, i) => (
              <div
                key={columns[i]}
                style={{
                  ...cellStyle,
                  gridColumn: i + 2,
                  gridRow: rules.length + 3,
                  fontWeight: 'var(--fw-medium)',
                  color:
                    i > 0 && total !== totals[i - 1]
                      ? 'var(--fg-brand-strong)'
                      : undefined,
                }}
              >
                {formatCurrency(total)}
              </div>
            ))}
          </>
        )}
      </div>
      {counts.some((count) => count > 1) && (
        <small className="text-muted d-block mt-2">
          Ciclos com 2+ descontos ativos aparecem em amarelo — confira se a
          combinação é a esperada.
        </small>
      )}
    </div>
  );
}
